import { useEffect, useState } from 'react'
import { settingApi } from '@/api'
import { SETTINGS_TYPES } from '@/constant'
import { SettingsService } from '@/services/SettingsService'
import { deepEqualObject } from '@/helpers/deepEqualObject'

interface SettingsParams {
  key: string
  value: string
  type: string
}

interface ResponseUpdateSettings {
  status: number
  response?: { data: { message: string } }
}

type Callback = () => void

interface UseSettingsOptions<T> {
  saveKey: string
  type: string
  defaultValue?: T
  onSuccess?: Callback
  onError?: (error: unknown) => void
  optimisticUpdate?: boolean // Новая опция для управления оптимистическим обновлением
}

export function useSettings<T>(
  options: UseSettingsOptions<T> & { defaultValue?: never },
): {
  value: T | undefined
  setValue: (
    newValue: T,
    successCallback?: Callback,
    errorCallback?: Callback,
  ) => Promise<void>
  isLoading: boolean
  isUpdating: boolean
}

export function useSettings<T>(
  options: UseSettingsOptions<T> & { defaultValue: T },
): {
  value: T
  setValue: (
    newValue: T,
    successCallback?: Callback,
    errorCallback?: Callback,
  ) => Promise<void>
  isLoading: boolean
  isUpdating: boolean
}

export function useSettings<T>({
  saveKey,
  type,
  defaultValue,
  onSuccess,
  onError,
  optimisticUpdate,
}: UseSettingsOptions<T>) {
  const [value, setValue] = useState<T | undefined>(defaultValue)
  const [isLoading, setIsLoading] = useState(true)
  const [isUpdating, setIsUpdating] = useState(false)

  // Функция для обновления настроек
  const updateSetting = async (
    newValue: T,
    successCallback?: Callback,
    errorCallback?: Callback,
  ) => {
    const params: SettingsParams = {
      key: saveKey,
      value: JSON.stringify(newValue),
      type,
    }
    // Сохраняем предыдущее значение для отката в случае ошибки
    const previousValue = value

    // Оптимистическое обновление: сразу устанавливаем новое значение, если включено
    if (optimisticUpdate) {
      setValue(newValue)
    }

    setIsUpdating(true)

    try {
      const res: ResponseUpdateSettings = await settingApi.update(params)

      if (!res.status || res.status < 200 || res.status >= 300) {
        throw new Error('Failed to update settings')
      }

            // Обновляем localStorage
      if (type === SETTINGS_TYPES.MENU) {
        const menuSettings = JSON.parse(
          localStorage.getItem(SETTINGS_TYPES.MENU_SETTINGS) ?? '{}',
        )
        localStorage.setItem(
          SETTINGS_TYPES.MENU_SETTINGS,
          JSON.stringify(
            Object.assign(menuSettings, { [saveKey]: params.value }),
          ),
        )
      } else {
        const generalSettings = SettingsService.get(type)
        localStorage.setItem(
          type,
          JSON.stringify(
            Object.assign(generalSettings, { [saveKey]: params.value }),
          ),
        )
      } 

      // Если оптимистическое обновление отключено, устанавливаем значение после успешной отправки
      if (!optimisticUpdate) {
        setValue(newValue)
      }

      successCallback?.()
      onSuccess?.()
    } catch (error) {
      // Откатываем значение в случае ошибки, если использовалось оптимистическое обновление
      if (optimisticUpdate) {
        setValue(previousValue)
      }
      errorCallback?.()
      onError?.(error)
    } finally {
      setIsUpdating(false)
    }
  }

  // Загрузка начальных настроек
  useEffect(() => {
    const loadSettings = async () => {
      setIsLoading(true)

      try {
        const settings = SettingsService.get(type)

        if (
          typeof settings === 'object' &&
          settings !== null &&
          !(saveKey in settings)
        ) {
          // Если настройки отсутствуют, сохраняем defaultValue
          if (defaultValue !== undefined) await updateSetting(defaultValue)
          return
        }

        const loadedValue = JSON.parse(settings?.[saveKey])
        if (!deepEqualObject(loadedValue, value)) setValue(loadedValue as T)
      } catch (error) {
        onError?.(error)
        if (defaultValue !== undefined) await updateSetting(defaultValue)
      } finally {
        setIsLoading(false)
      }
    }

    loadSettings()
  }, []) // оставить пустым

  return {
    value,
    setValue: updateSetting,
    isLoading,
    isUpdating,
  }
}
